import {Button, Modal} from 'antd'
import {
    game_createGame_Api,
    game_deleteGame_Api,
    game_editGame_Api,
    game_getById_Api,
    game_getGamesByOwner_Api
} from 'apis'
import {getId, getType} from 'apis/model/base'
import useTranslate from 'modules/local/useTranslate'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import React, {useMemo, useState} from 'react'
import {compose, fromRenderProps} from 'recompose'
import {bindQueryParams} from '../../AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey} from '../../AppLocals/hooks/usePaginationHelper'
import {getResponseItem, NullAsDefault} from 'views/Shared'
import CRUD from 'views/Shared/enums/CRUD'
import {notifyOnError} from '../../AppLocals/factory/createErrorEvent'
import {NotificationActionTypes, successNotify} from '../../AppLocals/factory/createNotification'
import {showDeleteConfirmDialog} from '../../AppLocals/factory/createConfirmDialog'
import {DelayRender} from 'views/Shared/components/DelayRender'
import {BiSolidEdit, BiTrash} from 'react-icons/bi'
import {TbListDetails} from 'react-icons/tb'
import WorkspaceContext from '../WorkspaceContext'
import {CreateGameForm, EditGameForm} from './GameForm'

const GAME_TYPE_LABELS = {
    single_choice: 'multiple choice',
    crossword: 'crossword',
    matching: 'matching pairs',
    ordering: 'ordering',
}

const STATUS_LABELS = {
    '-2': {label: 'rejected', className: 'text-red-500 bg-red-50'},
    '-1': {label: 'pending', className: 'text-orange-500 bg-orange-50'},
    '1': {label: 'open', className: 'text-green-600 bg-green-50'},
    '9': {label: 'closed', className: 'text-color-400 bg-color-50'},
}

// Sửa 1 game: KHÔNG dùng thẳng item từ danh sách (GameTransformerBasic - thiếu hearts_limit/
// point_per_question/is_fixed_questions...) mà tự gọi getById lấy full data (GameTransformer)
// rồi mới báo lên cha mở form edit. Rule chung: form sửa 1 item luôn phải nạp đủ thông tin qua
// getById, không tái dùng data rút gọn từ list. Fetch + loading state đặt NGAY TRONG GameRow
// (không đẩy loadingId lên state của Games) vì renderItem của usePaginationHelper bị cache theo
// withKey - loadingEditId đổi không nằm trong withKey nên list cha sẽ không re-render, prop rơi
// vào tình trạng cũ (đã gặp bug y hệt ở GameQuestions.js trước đó).
const GameRow = ({item, onEdit, onManageQuestions, onDelete}) => {
    const t = useTranslate()
    const {id, title, primary_answer_type, status, total_questions_available, total_plays} = item ?? {}
    const statusInfo = STATUS_LABELS[String(status)] ?? STATUS_LABELS['1']

    const {handleAsyncAction: fetchGameById, isLoading: isLoadingEdit} = useAsyncAction({
        apiInfo: game_getById_Api,
        onSuccess: (result, data) => onEdit(getResponseItem(data.response)),
        onError: notifyOnError(t),
    })

    return (
        <div className="p-3 flex flex-col gap-2 custom-item-render relative list-item">
            <div className="flex items-center justify-between gap-2">
                <h3
                    onClick={() => onManageQuestions(item)}
                    className="font-bold text-lg text-color-000 hover:text-primary truncate cursor-pointer">
                    {title}
                </h3>
            </div>
            <div className="flex items-center gap-3 text-color-300 tracking-wider">
                <span className="font-medium text-gray-700">{t(GAME_TYPE_LABELS[primary_answer_type] ?? primary_answer_type)}</span>
                <span>{'•'}</span>
                <span>{(total_questions_available ?? 0)} {t('questions')}</span>
                <span>{'•'}</span>
                <span>{(total_plays ?? 0)} {t('plays')}</span>
            </div>
            <span
                style={{padding: '2px 8px', width: 'fit-content', top: 10, right: 10}}
                className={'text-2xs font-bold rounded-full flex-shrink-0 absolute ' + statusInfo.className}>
                {t(statusInfo.label)}
            </span>
            <div className="flex items-center justify-end gap-4 pt-1 contextIcon">
                <button
                    type="button"
                    onClick={() => onManageQuestions(item)}
                    className="flex items-center gap-1 font-semibold text-xs text-purple-600 hover:text-purple-700 cursor-pointer">
                    <TbListDetails size={14}/>
                    {t('manage questions')}
                </button>
                <button
                    type="button"
                    disabled={isLoadingEdit}
                    onClick={() => fetchGameById({}, {id})}
                    className="flex items-center gap-1 font-semibold text-xs text-blue-500 hover:text-blue-700 cursor-pointer disabled:opacity-50">
                    <BiSolidEdit size={14}/>
                    {isLoadingEdit ? t('loading...') : t('edit')}
                </button>
                <button
                    type="button"
                    onClick={() => onDelete(id)}
                    className="flex items-center gap-1 font-semibold text-xs text-primary-500 hover:text-primary cursor-pointer">
                    <BiTrash size={14}/>
                    {t('delete')}
                </button>
            </div>
        </div>
    )
}

const Games = ({owner_id, owner_type, onNavigateQuestions}) => {
    const t = useTranslate()
    const [action, setAction] = useState({type: undefined, value: undefined})
    const [refreshKey, setRefreshKey] = useState()

    const onCancel = () => setAction({})
    const onSuccess = (message) => (result, data) => {
        successNotify(message, t)
        onCancel()
        setRefreshKey(Date.now())
    }

    const {handleAsyncAction: onCreate} = useAsyncAction({
        apiInfo: game_createGame_Api,
        onSuccess: onSuccess(NotificationActionTypes.ADD),
        onError: notifyOnError(t),
    })
    const {handleAsyncAction: onUpdate} = useAsyncAction({
        apiInfo: game_editGame_Api,
        onSuccess: onSuccess(NotificationActionTypes.UPDATE),
        onError: notifyOnError(t),
    })
    const {handleAsyncAction: onDelete} = useAsyncAction({
        apiInfo: game_deleteGame_Api,
        onSuccess: () => setRefreshKey(Date.now()),
        onError: notifyOnError(t),
    })

    const [pager_widget] = usePaginationHelper({
        sortHeader: null,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        renderFilter: NullAsDefault,
        AdvancedFilter: NullAsDefault,
        query_params: {keyword: 'workspace-game-keyword'},
        withKey: createPagerKey([owner_id, owner_type, refreshKey]),
        pagerInfo: {
            RenderEntity: React.Fragment,
            query: bindQueryParams([{id: owner_id}, {type: owner_type}]),
            PaginationMeta: NullAsDefault,
            apiInfo: game_getGamesByOwner_Api,
            renderItem: (item, index) => (
                <GameRow
                    key={index}
                    item={item}
                    onEdit={(value) => setAction({type: CRUD.UPDATE, value})}
                    onManageQuestions={onNavigateQuestions}
                    onDelete={(id) => showDeleteConfirmDialog({
                        translate: t,
                        title: 'delete game',
                        message: 'are you sure you want to delete this game',
                        onOk: () => onDelete({}, {id}),
                    })}
                />
            ),
            renderWrap: (child) => (
                <div className="flex flex-col divide-y divide-gray-200 list-item-workspace">
                    {child}
                </div>
            ),
        },
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-3">
                {searchBar}
                {filterBar}
            </div>
        ),
    })

    const modal = useMemo(() => {
        let Content = NullAsDefault
        let params = {}
        let modal_params = {}
        let title = null

        switch (action.type) {
            case CRUD.CREATE:
                title = t('create game')
                modal_params = {
                    width: 720,
                }
                params = {onSubmit: (values) => onCreate(values, {type: owner_type, id: owner_id})}
                Content = CreateGameForm
                break
            case CRUD.UPDATE:
                title = t('edit game')
                modal_params = {
                    width: 720,
                }
                params = {item: action.value, onSubmit: (values) => onUpdate(values, {id: action.value?.id})}
                Content = EditGameForm
                break
            default:
                break
        }

        return (
            <Modal
                footer={null}
                onCancel={onCancel}
                open={!!action.type}
                destroyOnHidden={true}
                className="custom-modal"
                title={<div className="text-center font-bold text-color-000 uppercase tracking-wide">{title}</div>}
                {...modal_params}>
                <DelayRender lazy={true}>
                    <Content {...params}/>
                </DelayRender>
            </Modal>
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action])

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col md:flex-row md:items-center gap-3">
                <div className="flex flex-col flex-1">
                    <h2 className="font-bold text-xl text-gray-800 tracking-wide">{t('kristo games')}</h2>
                    <div className="mt-1 w-20 h-1 rounded-full bg-gray-400"/>
                </div>
                <Button
                    color="primary" variant="solid"
                    className="font-semibold rounded-lg no-border"
                    onClick={() => setAction({type: CRUD.CREATE})}>
                    {t('create')}
                </Button>
            </div>
            {pager_widget}
            {modal}
            <div className="h-16"/>
        </div>
    )
}

export default compose(
    fromRenderProps(WorkspaceContext.Consumer, ({organization}) => ({
        owner_id: getId(organization),
        owner_type: getType(organization),
    }))
)(Games)
